import { useState } from "react"
import { ArrowRight } from "lucide-react"
import { cn } from "@/lib/utils"
import { Button } from "../Button/Button"

export interface NewStrategyFormProps {
  heading: string
  subheading?: string
  badgeLabel?: string
  placeholder?: string
  submitLabel?: string
  onSubmit?: (prompt: string) => void
  isSubmitting?: boolean
  error?: string | null
  className?: string
}

export function NewStrategyForm({
  heading,
  subheading,
  badgeLabel,
  placeholder,
  submitLabel = "Submit",
  onSubmit,
  isSubmitting = false,
  error = null,
  className,
}: NewStrategyFormProps) {
  const [draft, setDraft] = useState("")
  const hasInput = draft.trim().length > 0
  const canSubmit = hasInput && !isSubmitting

  const submit = () => {
    if (!canSubmit) return
    onSubmit?.(draft.trim())
  }

  return (
    <div className={cn("h-full overflow-y-auto", className)}>
      <div className="flex flex-col items-center gap-10 md:gap-12 px-5 md:px-6 pt-10 md:pt-[150px] pb-24 md:pb-[200px]">
        <div className="w-full max-w-[640px] flex flex-col gap-5">
          <div className="text-center">
            {badgeLabel && (
              <div className="inline-flex items-center gap-1.5 bg-muted rounded-md px-2.5 py-1 mb-5">
                <div className="w-1.5 h-1.5 rounded-full bg-primary animate-pulse" />
                <span className="text-caption text-muted-foreground tracking-wide">
                  {badgeLabel}
                </span>
              </div>
            )}
            <h1 className="text-page-title sm:text-title text-foreground mb-2.5">{heading}</h1>
            {subheading && (
              <p className="text-body text-muted-foreground leading-relaxed max-w-[360px] mx-auto">
                {subheading}
              </p>
            )}
          </div>
          <div className="bg-popover border border-border rounded-lg p-3">
            <textarea
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault()
                  submit()
                }
              }}
              placeholder={placeholder}
              rows={3}
              aria-label="Strategy prompt"
              disabled={isSubmitting}
              className="w-full bg-transparent border-none outline-none text-foreground text-body leading-relaxed resize-none placeholder:text-muted-foreground/50 disabled:opacity-60"
            />
            <div className="flex items-center justify-end mt-2">
              <Button
                size="sm"
                disabled={!canSubmit}
                onClick={submit}
                className="gap-1.5 text-label"
                aria-label={submitLabel}
              >
                <ArrowRight size={12} strokeWidth={2} />
                {submitLabel}
              </Button>
            </div>
          </div>
          {error && (
            <p className="text-caption text-destructive text-center" role="alert">
              {error}
            </p>
          )}
        </div>
      </div>
    </div>
  )
}
